/**
 * Phase 20 — the open campaign group's auto-send, beside its master config
 * in the containers card header: its states (Off · Simulated · Live — Live
 * asks first, real SMS; Simulated decides on the real containers and sends
 * nothing),
 * today's fact on wider screens, Resume while its stop-loss halts it, and
 * its settings (the master config's Auto-send tab). The same pills as its
 * row in the country's groups table.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Play, SlidersHorizontal } from 'lucide-react';
import { autoSendModeOf, type DropGenGroupSendStatus, type DropGenServerSendModes } from '@trafficpulse/shared';

import { Button, SegmentedPills } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { useSchedulerStatus } from '../../../useDropGenerator';
import { AutoSendConfirmDialog } from '../../board-header/autosend/AutoSendConfirmDialog';
import { groupFact } from '../../board-header/autosend/autosend-fact';
import { groupLayer, groupRows } from '../../board-header/autosend/groups/group-rows';
import { modeOptions, useModeRights } from '../../board-header/autosend/groups/mode-options';
import { TONE } from '../../board-header/autosend/groups/GroupSendRow';
import { useGroupSwitch } from '../../board-header/autosend/groups/use-group-switch';

export const GroupAutoSendControl: FC<{ plan: DropGenPlanDetail; masterId: string; onSettings: () => void }> = ({ plan, masterId, onSettings }) => {
  const status = useSchedulerStatus().data;
  const mine = status?.plans.find((p) => p.countryKey === plan.countryKey);
  const group = groupRows(plan, mine).find((g) => g.masterId === masterId);
  return group ? <Control plan={plan} group={group} server={status?.sendModes} onSettings={onSettings} /> : null;
};

const Control: FC<{ plan: DropGenPlanDetail; group: DropGenGroupSendStatus; server: DropGenServerSendModes | undefined; onSettings: () => void }> = ({ plan, group, server, onSettings }) => {
  const sw = useGroupSwitch(plan.countryKey, group);
  const fact = groupFact(group, plan.recipientTimezone, server);
  const current = autoSendModeOf(group.config);
  const options = modeOptions(useModeRights(), current);
  return (
    <span className="flex h-9 min-w-0 max-w-[420px] items-center gap-2 rounded-md border border-input bg-background px-2.5 text-xs" data-testid="group-autosend">
      <span className="whitespace-nowrap font-medium">Auto-send</span>
      <span className={sw.canSwitch ? '' : 'pointer-events-none opacity-50'} aria-label={`Auto-send for ${group.name}`}>
        <SegmentedPills options={options} value={current} onChange={sw.pick} className="[&_button]:h-6 [&_button]:px-2 [&_button]:text-[11px]" />
      </span>
      <span className={`hidden min-w-0 truncate md:inline ${TONE[fact.tone]}`} title={fact.text}>{fact.text}</span>
      {group.haltedToday && sw.canResume ? (
        <Button size="sm" variant="outline" className="h-7 px-2" loading={sw.resuming} onClick={sw.resume}><Play className="mr-1 h-3 w-3" />Resume</Button>
      ) : null}
      <button
        type="button"
        onClick={onSettings}
        aria-label={`Auto-send settings of ${group.name}`}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
      </button>
      <AutoSendConfirmDialog open={sw.confirming} plan={plan} layer={groupLayer(group)} today={group.sendToday} mode={sw.confirmMode} busy={sw.busy} onConfirm={sw.confirm} onCancel={sw.cancel} />
    </span>
  );
};
